"use client"

import { useCallback, useEffect, useState } from "react"
import { Navigate, Outlet, useLocation } from "react-router-dom"
import { Box, Flex, useBreakpointValue } from "@chakra-ui/react"

import Sidebar from "./Sidebar"
import TopBar from "./TopBar"
import NotificationPopup from "../NotificationPopup"
import NewDataPopup from "../newData/NewDataPopup"
import SupplierUnpaidNotice from "../shared/SupplierUnpaidNotice"
import NoSupplierNotice from "../shared/NoSupplierNotice"
import TeleportBadge from "../TeleportBadge"
import Footer from "../Footer"

import { useAuthentication } from "../../wrappers/UseAuthentication"
import { useSupplierRetailer } from "../../wrappers/SupplierRetailerContext"
import { usePageAccess } from "../../wrappers/PageAccessContext"
import { neutral, layout } from "../../theme/tokens"

const COLLAPSE_KEY = "spearbi_nav_collapsed"

// Pages where the retailer channel is not part of the question being asked.
// Showing the rail there implied the page was filtered by it.
const CHANNEL_FREE_ROUTES = [
  "/myProfile",
  "/users",
  "/addUser",
  "/loginhistory",
  "/supplierLogos",
  "/organisations",
  "/org-reports",
  "/editPageAccess",
  "/editSupplierAccess",
  "/editRetailerAccess",
  "/rep", // combines every supplier of the organisation, Dischem only — no channel rail (2026-09-21)
  "/field", // rep tracker / my route — organisation-wide, Dischem stores (2026-10-06)
]

// Report routes that read the selected supplier's data. When that supplier's
// account is blocked for non-payment and the viewer is not a master, the shell
// shows SupplierUnpaidNotice here instead of mounting the page — one gate for
// all five, and no request ever reaches the backend's 403.
const SUPPLIER_REPORT_ROUTES = {
  "/sales": "Sales report",
  "/insights": "Insights",
  "/strike": "Strike Rate report",
  "/ranging": "Ranging report",
  "/margin": "Margin report",
}

const AuthenticatedLayout = () => {
  const { signOut, teleport } = useAuthentication()
  const { retailers, user_supplier_access, supplierBlocked, loading: supplierLoading } = useSupplierRetailer()
  const { user_page_access, loading: pageLoading, restrictedyn, trialyn, paymentReminder } = usePageAccess()
  const location = useLocation()

  const [isNotificationOpen, setIsNotificationOpen] = useState(false)
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSE_KEY) === "1" || (typeof window !== "undefined" && window.innerWidth < 640))
  const [isAdmin, setIsAdmin] = useState(false)

  // Phone (< 768px, 2026-10-13 Helena): the rail no longer sits beside the content — a 64px rail on a
  // 390px screen left ~300px for the Scorecard / My route pages. It becomes a slide-in drawer opened
  // from the top bar's menu button, closed by the backdrop or by navigating. Tablet / desktop unchanged.
  const isPhone = useBreakpointValue({ base: true, md: false }, { ssr: false }) === true
  const [mobileOpen, setMobileOpen] = useState(false)
  useEffect(() => { setMobileOpen(false) }, [location.pathname])

  // Re-evaluated on every teleport in/out (2026-09-17): while a master is
  // signed in as someone else the shell must show THAT account's rail — no
  // master-only items, no New Data popup — and get them back afterwards.
  useEffect(() => {
    const userid = localStorage.getItem("userid")
    setIsAdmin(userid === "1" || userid === "2")
  }, [teleport])

  useEffect(() => {
    if (paymentReminder) setIsNotificationOpen(true)
  }, [paymentReminder])

  const toggleCollapse = useCallback(() => {
    setCollapsed((prev) => {
      localStorage.setItem(COLLAPSE_KEY, prev ? "0" : "1")
      return !prev
    })
  }, [])

  const channelRoute =
    retailers.length > 0 && !CHANNEL_FREE_ROUTES.some((route) => location.pathname.startsWith(route))

  // The caller's OWN restriction wins: a suspended supplier account keeps the
  // page-level "Account payment required → Go to my profile" screen (Sales.js,
  // Insights.js), which this notice would otherwise cover.
  const blockedRoute =
    supplierBlocked && !restrictedyn
      ? Object.keys(SUPPLIER_REPORT_ROUTES).find((route) => location.pathname.startsWith(route))
      : null

  // An account with NO supplier at all (an org admin created before its
  // organisation has one — 2026-09-16) gets NoSupplierNotice on the report
  // routes instead of the page: the report components crash on an empty
  // supplier. Only once the access fetch has answered — an empty list while
  // loading is not "no suppliers".
  // A FIELD-ONLY login — every page it holds is a Field page: Scorecard (Curaden's CURADEN) and/or
  // RepRoute (a Blue Mango rep), 2026-10-06 — sees nothing else: whatever address it opens, the /sales
  // landing after sign-in included, it is kept on its Field page. The report APIs refuse it too (backend
  // middleware/denyScorecardOnly.js).
  const FIELD_HOME = { RepRoute: "/field/route", Scorecard: "/scorecard" }
  const grantedPages = (user_page_access || []).map((p) => p.value)
  const fieldOnly = !isAdmin && !pageLoading && grantedPages.length > 0 && grantedPages.every((p) => FIELD_HOME[p])
  const fieldHome = fieldOnly ? FIELD_HOME[grantedPages.includes("RepRoute") ? "RepRoute" : "Scorecard"] : null
  const scorecardOnly =
    fieldOnly && !grantedPages.some((p) => location.pathname.startsWith(FIELD_HOME[p]))

  // Phone + a Scorecard-only login (Curaden's shared CURADEN, 2026-10-13 Helena: "hide the left side … the rep
  // does not have to see that"): no rail, no ☰ — the page is the whole app. A rep login (RepRoute) keeps the
  // drawer because the supplier picker for the store page lives in it. Desktop unchanged.
  // Any size since the same afternoon ("the left blue block is still showing … you can even remove the top
  // section with the retailer logos"): the Scorecard picks its one programme itself (Scorecard.js), so the
  // supplier / retailer controls add nothing for that login.
  // 2026-10-13 (later, Helena on the rep login): "move the supplier drop-down to the right side so we can get rid
  // of the block on the left" — EVERY field-only login (Curaden's Scorecard, a Blue Mango rep's My route) loses
  // the rail at any size; a rep gets a compact supplier picker in the top bar instead (TopBar supplierPicker).
  const hideRail = fieldOnly
  const showChannels = channelRoute && !hideRail

  const noSupplierRoute =
    !supplierLoading && user_supplier_access.length === 0
      ? Object.keys(SUPPLIER_REPORT_ROUTES).find((route) => location.pathname.startsWith(route))
      : null

  return (
    // No `overflow: hidden` on the shell — that is what would stop the command
    // bar's `position: sticky` from ever engaging.
    <Box bg={neutral[50]} minH="100vh">
      <NotificationPopup
        key={paymentReminder}
        isOpen={isNotificationOpen}
        onClose={() => setIsNotificationOpen(false)}
        message={paymentReminder}
      />

      <NewDataPopup enabled={isAdmin} />

      {/* Purple "Teleported in · Teleport out" pill — renders only for a master mid-teleport. */}
      <TeleportBadge />

      {!hideRail && <Sidebar
        collapsed={isPhone ? false : collapsed}
        onToggleCollapse={toggleCollapse}
        userPageAccess={pageLoading ? [] : user_page_access}
        restrictedyn={restrictedyn}
        hasSuppliers={user_supplier_access.length > 0}
        isAdmin={isAdmin}
        isPhone={isPhone}
        mobileOpen={mobileOpen}
      />}

      {/* Phone drawer backdrop — tap anywhere outside the rail to close it. */}
      {isPhone && !hideRail && mobileOpen && (
        <Box position="fixed" inset={0} bg="rgba(10,20,30,0.45)" zIndex={990} onClick={() => setMobileOpen(false)} aria-hidden />
      )}

      <Flex
        direction="column"
        minH="100vh"
        ml={isPhone || hideRail ? 0 : collapsed ? layout.sidebarWidthCollapsed : layout.sidebarWidth}
        transition="margin-left 260ms cubic-bezier(0.2, 0, 0.38, 0.9)"
      >
        <TopBar onSignOut={signOut} trialyn={trialyn} showChannels={showChannels} onMenu={isPhone && !hideRail ? () => setMobileOpen((o) => !o) : null} supplierPicker={hideRail} />

        <Box as="main" className="sp-main" flex="1" px={layout.contentGutter} py={4} minW={0}>
          {scorecardOnly ? (
            <Navigate to={fieldHome} replace />
          ) : blockedRoute ? (
            <SupplierUnpaidNotice pageTitle={SUPPLIER_REPORT_ROUTES[blockedRoute]} />
          ) : noSupplierRoute ? (
            <NoSupplierNotice pageTitle={SUPPLIER_REPORT_ROUTES[noSupplierRoute]} />
          ) : (
            <Outlet />
          )}
        </Box>

        <Footer />
      </Flex>
    </Box>
  )
}

export default AuthenticatedLayout
